// 변경공사 화면 경로·표시 — 클라이언트도 쓰므로 해시(node:crypto)를 딸고 오는 모듈을 부르지 않는다.

import { formatWon } from "../../lib/format";
import { checked } from "../money/checked";
import type { VatMode } from "../money/vat";
import { reloadedNoticeOf, revReloadedNoticeOf } from "./messages";
import { CHANGE_ORDER_ACTIVE_PROJECT } from "./transitions";
import type { ChangeOrder, ChangeOrderSnapshotData } from "./types";
import type { Contract } from "../contract/rows";
import type { ProjectLog, ProjectLogKind, ProjectStatus } from "../project/types";

export const changeOrderPath = (contractId: string, changeOrderId: string): string =>
  `/app/contracts/${contractId}/changes/${changeOrderId}`;

export const newChangeOrderPath = (contractId: string, logId?: string): string =>
  `/app/contracts/${contractId}/changes/new${logId ? `?log=${encodeURIComponent(logId)}` : ""}`;

// 변경공사를 만들 수 있는 자리 — 체결 계약 + 착공 전·시공 중·유지보수 현장(RPC 도 같은 조건을 본다).
export const canStartChangeOrder = (
  contractStatus: Contract["status"],
  projectStatus: ProjectStatus | null | undefined
): boolean =>
  contractStatus === "SIGNED" && projectStatus != null && CHANGE_ORDER_ACTIVE_PROJECT.includes(projectStatus);

export interface PaymentCompareRow {
  seq: number;
  label: string;
  before: number | null;
  after: number | null;
  paid: boolean;
}

// 회차 전·후 대조 줄 — 변경 후에만 있는 회차(정산 회차)는 전 금액 없이 끝에 붙는다.
export const paymentCompareRows = (
  data: Pick<ChangeOrderSnapshotData, "payments_before" | "payments_after">
): PaymentCompareRow[] => {
  const afterBySeq = new Map(data.payments_after.map((p) => [p.seq, p]));
  const beforeSeqs = new Set(data.payments_before.map((p) => p.seq));
  return [
    ...data.payments_before.map((p) => ({
      seq: p.seq,
      label: p.label,
      before: p.amount,
      after: afterBySeq.get(p.seq)?.amount ?? null,
      paid: p.paid,
    })),
    ...data.payments_after
      .filter((p) => !beforeSeqs.has(p.seq))
      .map((p) => ({ seq: p.seq, label: p.label, before: null, after: p.amount, paid: p.paid })),
  ];
};

// 증감액 표시 — 추가는 +, 감액은 -(0 은 부호 없음).
export const signedWon = (value: number): string =>
  `${value > 0 ? "+" : value < 0 ? "-" : ""}${formatWon(Math.abs(value))}`;

// 증감 글자색 — 늘면 warning, 줄면 status-done(0 은 기본색).
export const deltaToneClass = (amount: number): string | undefined =>
  amount > 0 ? "text-warning" : amount < 0 ? "text-status-done" : undefined;

// 스냅샷의 부가세 기준 칸이 먼저 · 없는 옛 문서는 줄 합이 증감(포함)과 다르고 공급가 증감과 같으면 별도 계약(줄 금액 = 공급가)으로 읽는다.
export const changeOrderSnapshotVatModeOf = (
  data: Pick<ChangeOrderSnapshotData, "delta_total" | "delta_supply" | "vat_mode"> & {
    lines: readonly Pick<ChangeOrderSnapshotData["lines"][number], "direction" | "amount">[];
  }
): VatMode => {
  if (data.vat_mode) return data.vat_mode;
  const net = data.lines.reduce((acc, l) => checked(acc + (l.direction === "DEDUCT" ? -l.amount : l.amount), "증감액"), 0);
  return net !== data.delta_total && net === data.delta_supply ? "EXCL" : "INCL";
};

// 공기 증감 표시 — 작업일 기준(0·없음은 빈 문자열).
export const extendDaysLabel = (days: number | null | undefined): string =>
  days ? `공기 ${days > 0 ? "+" : "-"}${Math.abs(days)}일(작업일)` : "";

// 일지에서 온 변경공사의 제목·사유 — 추가공정·노쇼만 받고 항목은 비운다(노쇼는 금액이 아니라 일정일 수 있다).
const LOG_TITLE_PREFIX: Partial<Record<ProjectLogKind, string>> = {
  EXTRA_WORK: "추가공정",
  NO_SHOW: "작업자 노쇼",
};

export const CHANGE_ORDER_LOG_KINDS: readonly ProjectLogKind[] = ["EXTRA_WORK", "NO_SHOW"];

// 「변경공사로」 후보 일지 — 거절·파기된 건은 금액에 반영되지 않았으니 그 일지로 다시 만들 수 있게 둔다.
export const changeableLogFilter = (
  changeOrders: readonly Pick<ChangeOrder, "status" | "source_log_id">[]
): ((log: Pick<ProjectLog, "id" | "kind">) => boolean) => {
  const linked = new Set(
    changeOrders.filter((co) => co.status !== "VOID" && co.status !== "REJECTED").map((co) => co.source_log_id)
  );
  return (log) => CHANGE_ORDER_LOG_KINDS.includes(log.kind) && !linked.has(log.id);
};

export const prefillFromLog = (
  log: Pick<ProjectLog, "id" | "kind" | "body">
): { title: string; reason: string; source_log_id: string } | null => {
  const prefix = LOG_TITLE_PREFIX[log.kind];
  if (!prefix) return null;
  const first = log.body.split("\n").find((line) => line.trim() !== "")?.trim() ?? "";
  const head = first.length > 60 ? `${first.slice(0, 60)}…` : first;
  return { title: head ? `${prefix}: ${head}` : prefix, reason: log.body.trim(), source_log_id: log.id };
};

// 다시 읽은 뒤 서버 표를 새로고침해도 되는지 — 새 변경공사이거나 서버 rev 가 편집기 rev 와 같을 때만(다르면 key={co.rev} 로 재마운트돼 미저장 항목이 사라진다).
export const refreshAfterReload = (editorRev: number | null, serverRev: number | null): boolean =>
  editorRev === null || serverRev === editorRev;

// 다시 읽은 뒤 할 일 — rev 가 바뀌면 고친 게 없을 때만 새로고침하고 있으면 옛 값(stale) 안내 · 내 저장과 겹친 읽기(pending)는 rev 를 보지 않는다.
export const reloadPlanOf = (input: {
  editorRev: number | null;
  serverRev: number | null;
  paymentsChanged: boolean;
  totalChanged: boolean;
  dirty: boolean;
  pending?: boolean;
}): { notice: string | null; refresh: boolean; stale: boolean } => {
  const notice = reloadedNoticeOf(input.paymentsChanged, input.totalChanged);
  if (input.pending) return { notice, refresh: false, stale: false };
  const revChanged = input.editorRev !== null && input.serverRev !== null && input.serverRev !== input.editorRev;
  if (revChanged && !input.dirty) return { notice: revReloadedNoticeOf(notice), refresh: true, stale: false };
  if (revChanged) return { notice, refresh: false, stale: true };
  return { notice, refresh: notice !== null && refreshAfterReload(input.editorRev, input.serverRev), stale: false };
};

// 지급회차 목록이 같은지 — 다시 읽은 값이 그대로면 미리보기를 건드리지 않는다(회차 순서·비율까지 본다).
type PaymentRowKey = { id: string; seq: number; ratio_bp: number; amount: number; paid_at: string | null };
export const samePaymentRows = (a: readonly PaymentRowKey[], b: readonly PaymentRowKey[]): boolean =>
  a.length === b.length &&
  a.every((p, i) => p.id === b[i].id && p.seq === b[i].seq && p.ratio_bp === b[i].ratio_bp && p.amount === b[i].amount && p.paid_at === b[i].paid_at);

// 앞 호출이 끝나기 전 호출은 건너뛴다 — 창 복귀 때 focus·visibilitychange 가 같이 와도 한 번만 다시 읽는다.
export const singleFlight = (run: () => Promise<void>): (() => Promise<void>) => {
  let busy = false;
  return async () => {
    if (busy) return;
    busy = true;
    try {
      await run();
    } finally {
      busy = false;
    }
  };
};

// 상세 탭 제목 — 계약번호·차수 뒤에 제목을 붙이고, 긴 제목은 탭에서 잘리므로 30자에서 「…」로 줄인다.
export const CHANGE_ORDER_TAB_TITLE_MAX = 30;
export const changeOrderTabTitle = (contractNo: string, seq: number, title: string): string => {
  const head = `변경공사 ${contractNo} ${seq}차`;
  const name = title.trim();
  if (!name) return head;
  // 코드포인트 단위로 센다 — UTF-16 으로 자르면 이모지가 반쪽 난다.
  const chars = Array.from(name);
  const clipped = chars.length > CHANGE_ORDER_TAB_TITLE_MAX ? `${chars.slice(0, CHANGE_ORDER_TAB_TITLE_MAX).join("")}…` : name;
  return `${head} · ${clipped}`;
};

export const CHANGE_ORDER_FREE_LINE_LABEL = "추가 항목";

// 연결 없는 감액(옛 행) — 현재 내역서의 묶음 이름과 같은 말.
export const CHANGE_ORDER_LEGACY_DEDUCT_LABEL = "기타 감액";

interface SourceTagLine {
  direction: "ADD" | "DEDUCT";
  source_label?: string | null;
  trade_name?: string | null;
  space_label?: string | null;
}

// 항목 아래 한 줄(담당자 화면) — 공종·공간과 「원 견적: …」, 연결 없는 추가는 「추가 항목」·감액은 「기타 감액」(렌더 때 짓고 스냅샷엔 없다).
export const sourceTagOf = (line: SourceTagLine): { place: string | null; origin: string | null } => {
  const place = [line.trade_name, line.space_label].filter(Boolean).join(" · ") || null;
  if (line.source_label) return { place, origin: `원 견적: ${line.source_label}` };
  return { place, origin: line.direction === "ADD" ? CHANGE_ORDER_FREE_LINE_LABEL : CHANGE_ORDER_LEGACY_DEDUCT_LABEL };
};

// 굳은 문서(고객·인쇄) 줄 아래 글 — 스냅샷이 실은 원 견적 사본만 찍고 없으면 아무것도 붙이지 않는다(서명 뒤 새 글 금지).
export const documentSourceTagOf = (line: Omit<SourceTagLine, "direction">): string | null =>
  [line.trade_name, line.space_label, line.source_label ? `원 견적: ${line.source_label}` : null].filter(Boolean).join(" · ") ||
  null;

// 편집기 잔량 초과 문구 — 그 원 견적 줄을 가리키는 항목을 고치거나 지우면 내린다(다시 저장할 때 서버가 다시 본다).
export const overAfterRowEdit = <T extends { source: string }>(
  over: T | null,
  rows: readonly { key: number; source: string | null }[],
  key: number
): T | null => (over && rows.some((r) => r.key === key && r.source === over.source) ? null : over);

// 행을 고치거나 지워 경고가 걷히는 그 순간 알림도 닫는다(효과를 기다리면 지운 행의 토스트가 한 박자 남는다).
export const clearOverOnEdit = <T extends { source: string }>(
  over: T | null,
  rows: readonly { key: number; source: string | null }[],
  key: number,
  dismiss: () => void
): T | null => {
  const next = overAfterRowEdit(over, rows, key);
  if (over && !next) dismiss();
  return next;
};
